import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Alert, Button, Spin } from "antd";
import { observer } from "mobx-react-lite";

import {
  createPaymentMethod,
  updateAutopaymentStatus,
} from "../../api/payment";
import { teamStore } from "../../stores/teamStore";
import { userStore } from "../../stores/userStore";
import { sendCounterEvent } from "../../utils/counterService";
import { formatDate } from "../../utils/date";
import { formatPrice } from "../../utils/price";
import { getSubscriptionPlanName } from "../../utils/subscriptionPlan";
import { usePendingAction } from "../../utils/usePendingAction";
import { SubscriptionRestoreModal } from "./SubscriptionRestoreModal";

import "./SubscriptionInfo.css";

// DEV: set to true to simulate payment method change cycle (3s spinner → 3s method → repeat)
const DEV_SIMULATE_PAYMENT_METHOD = false;

export const SubscriptionInfo = observer(
  ({ pollingPaymentMethod = false }: { pollingPaymentMethod?: boolean }) => {
    const { t, i18n } = useTranslation();
    const sub = userStore.subscription;
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const { pending: restoring, run: runRestore } = usePendingAction();
    const { pending: isRedirecting, run: runChangePaymentMethod } =
      usePendingAction();

    const handleChangePaymentMethod = async () => {
      await runChangePaymentMethod(async () => {
        try {
          const data = await createPaymentMethod();
          if (data.redirect_url) {
            window.location.href = data.redirect_url;
          }
        } catch (error) {
          console.error("Redirect failed", error);
        }
      });
    };

    if (!sub) return <Spin />;

    const plan = sub.plan?.toLowerCase() ?? "lemon";
    const autopayment = sub.autopayment;
    // const mockPaymentMethod = {
    //   type: "card",
    //   card: { brand: "visa", last4: "4242", exp_month: 12, exp_year: 2028 },
    // };
    const paymentMethodString = getPaymentMethodString(
      sub.payment_method,
      // ?? mockPaymentMethod,
    );
    const expireAt = sub.expire_at
      ? formatDate(sub.expire_at, i18n.language)
      : null;
    const canceledAt = sub.canceled_at;

    const handleRestore = async () => {
      await runRestore(async () => {
        sendCounterEvent("restore-subscription");
        try {
          await updateAutopaymentStatus("active");
          await userStore.reloadSubscription();
          setRestoreDialogOpen(false);
        } catch {
          // silent
        }
      });
    };

    const navigate = useNavigate();
    const trialActive = sub.trial_active;
    const cancelAction = {
      text: t("subscription.cancelSubscription"),
      link: "/subscription/update/cancel",
      primary: false,
    };
    const actions = trialActive
      ? [cancelAction]
      : getActions(plan, autopayment, t);

    // DEV: simulate payment method change cycle (3s spinner → 3s method → repeat)
    const [devStable, setDevStable] = useState(true);
    useEffect(() => {
      if (!DEV_SIMULATE_PAYMENT_METHOD) return;
      const id = setInterval(() => setDevStable((s) => !s), 3000);
      return () => clearInterval(id);
    }, []);
    const isPaymentMethodStable = DEV_SIMULATE_PAYMENT_METHOD
      ? devStable
      : !pollingPaymentMethod;

    return (
      <div className="subscription-info">
        <div className="subscription-info__plan">
          <div className="subscription-info__label">
            {t("subscription.nextgisSubscription")}
          </div>

          <div
            className={`subscription-info__plan-chip subscription-info__plan-chip--${plan}`}
          >
            {plan !== "lemon" && sub.interval ? (
              <Trans
                i18nKey="subscription.planChip"
                context={sub.interval}
                values={{ plan: getSubscriptionPlanName(plan) }}
                components={{
                  period: (
                    <span className="subscription-info__plan-chip-interval" />
                  ),
                }}
              />
            ) : (
              getSubscriptionPlanName(plan)
            )}
          </div>
        </div>
        <div className="subscription-info__limits">
          <div className="subscription-info__limits-label">
            {t("subscription.currentLimits").toUpperCase()}
          </div>
          <div className="subscription-info__limits-tiles">
            {(() => {
              const u = t("subscription.unlimited");
              const s = sub.user_settings;
              return (
                <>
                  <div className="subscription-info__limits-tile">
                    <span className="subscription-info__limits-value">
                      {s?.user_limit ?? u}
                    </span>
                    <span className="subscription-info__limits-name">
                      {t("subscription.limitUsers")}
                    </span>
                  </div>
                  <div className="subscription-info__limits-tile">
                    <span className="subscription-info__limits-value">
                      {s?.storage_limit != null
                        ? formatBytes(s.storage_limit)
                        : u}
                    </span>
                    <span className="subscription-info__limits-name">
                      {t("subscription.limitStorage")}
                    </span>
                  </div>
                  {/* <div className="subscription-info__limits-tile">
                  <span className="subscription-info__limits-value">
                    {s?.resource_limit ?? u}
                  </span>
                  <span className="subscription-info__limits-name">
                    {t("subscription.limitResources")}
                  </span>
                </div> */}
                </>
              );
            })()}
          </div>
        </div>
        {plan === "lemon" && (
          <>
            <hr />
            <div className="subscription-info__free-hint">
              {t("subscription.freeUpgradeHint")}
            </div>
          </>
        )}
        {paymentMethodString && (
          <div className="subscription-info__payment-method">
            <hr />
            <div className="subscription-info__payment-method-caption">
              PAYMENT METHOD
            </div>
            <div className="subscription-info__payment-method-body">
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span>{paymentMethodString}</span>

                {!isPaymentMethodStable && (
                  <Spin
                    size="small"
                    style={{
                      transform: "scale(0.5)",
                      transformOrigin: "center center",
                    }}
                  />
                )}
              </div>
              {!canceledAt && (
                <Button
                  type="link"
                  loading={isRedirecting}
                  onClick={handleChangePaymentMethod}
                  style={{ padding: 0, height: "auto" }}
                >
                  {t("subscription.changePaymentMethod")}
                </Button>
              )}
            </div>
          </div>
        )}

        {expireAt && (
          <div className="subscription-info__date">
            <hr />
            {autopayment ? (
              <>
                <div className="subscription-info__payment-method-caption">
                  {t("subscription.upcomingCharge").toUpperCase()}
                </div>
                <div className="subscription-info__upcoming-charge-row">
                  <span>{expireAt}</span>
                  <span className="subscription-info__upcoming-charge-row-sum">
                    {formatPrice(parseFloat(sub.price), sub.currency)}
                  </span>
                </div>
              </>
            ) : (
              <>
                <span>
                  {t("subscription.until")} <strong>{expireAt}</strong>.
                </span>
                {canceledAt && (
                  <div className="subscription-info__after-cancel">
                    {t("subscription.afterCancelation")}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="subscription-info__actions">
          {canceledAt ? (
            <Button
              size="large"
              type="primary"
              block
              onClick={() => setRestoreDialogOpen(true)}
            >
              {t("subscription.restore")}
            </Button>
          ) : (
            <>
              {trialActive && (
                <Alert
                  type="info"
                  showIcon
                  description={
                    <Trans
                      i18nKey="subscription.trial.changePlanHint"
                      components={{
                        br: <br />,
                        lnk: <Link to="/support" />,
                      }}
                    />
                  }
                  style={{ marginBottom: 12 }}
                />
              )}
              {actions.map((action, i) => (
                <Button
                  size="large"
                  key={i}
                  type={action.primary ? "primary" : "default"}
                  block
                  onClick={() => navigate(action.link)}
                  className="subscription-info__action-btn"
                >
                  {action.text}
                </Button>
              ))}
            </>
          )}
        </div>

        <SubscriptionRestoreModal
          open={restoreDialogOpen}
          onClose={() => setRestoreDialogOpen(false)}
          onConfirm={handleRestore}
          loading={restoring}
          price={formatPrice(parseFloat(sub.price), sub.currency)}
          expireAt={expireAt}
        />
      </div>
    );
  },
);

function formatBytes(bytes: number): string {
  const gb = bytes / 1024 ** 3;
  return `${Math.round(gb)} GB`;
}

const CARD_BRAND_MAP: Record<string, string> = {
  amex: "American Express",
  cartes_bancaires: "Cartes Bancaires",
  diners: "Diners Club",
  discover: "Discover",
  eftpos_au: "Eftpos Australia",
  link: "Girocard",
  jcb: "JCB",
  mastercard: "MasterCard",
  unionpay: "UnionPay",
  visa: "Visa",
  unknown: "Unknown",
};

const PAYMENT_TYPE_MAP: Record<string, string> = {
  revolut_pay: "Revolut Pay",
  yoo_money: "ЮMoney",
  sberbank: "SberPay",
  sbp: "СБП",
};

function getPaymentMethodString(
  paymentMethod:
    | {
        type: string;
        card?: {
          brand: string;
          last4: string;
          exp_month: number;
          exp_year: number;
        };
      }
    | null
    | undefined,
): string {
  if (!paymentMethod) return "";
  const { type, card } = paymentMethod;
  if (type === "card" || type === "bank_card") {
    if (!card) return "";
    const brandName =
      type === "card"
        ? (CARD_BRAND_MAP[card.brand] ?? card.brand.toUpperCase())
        : card.brand;
    const expYear = String(card.exp_year).slice(2, 4);
    return `${brandName} ····${card.last4} ${card.exp_month}/${expYear}`;
  }
  return PAYMENT_TYPE_MAP[type] ?? type.replace(/_/g, " ").toUpperCase();
}

interface Action {
  text: string;
  link: string;
  primary: boolean;
}

function getActions(
  plan: string,
  autopayment: boolean,
  t: (key: string, opts?: Record<string, string>) => string,
): Action[] {
  if (autopayment) {
    if (plan === "lemon") {
      return [
        {
          text: t("subscription.upgrade"),
          link: "/subscription/update/plans",
          primary: true,
        },
      ];
    }
    return [
      {
        text: t("subscription.changePlan"),
        link: "/subscription/update/plans",
        primary: true,
      },
      {
        text: t("subscription.cancelSubscription"),
        link: "/subscription/update/cancel",
        primary: false,
      },
    ];
  }
  // No autopayment
  if (plan === "lemon") {
    return [
      {
        text: t("subscription.upgrade"),
        link: "/subscription/update/plans",
        primary: true,
      },
    ];
  }
  return [
    {
      text: t("subscription.changePlan"),
      link: "/subscription/update/plans",
      primary: true,
    },
  ];
}

export const TeamPlanAlert = observer(function TeamPlanAlert() {
  const { t } = useTranslation();
  if (!teamStore.isTeamMember) return null;
  return (
    <div className="subscription-team-plan-card">
      <div className="subscription-team-plan-card__header">
        {t("subscription.teamPlan")}: <strong>Premium</strong>
      </div>
      <p className="subscription-team-plan-card__desc">
        <Trans
          i18nKey="subscription.teamPlanDueTo"
          components={{ lnk: <Link to="/teammanage/member" /> }}
        />
      </p>
    </div>
  );
});
